import { useEffect, useState } from "react";

import { IdleConfirmDialog } from "@/components/IdleConfirmDialog";
import { useSharedNow } from "@/hooks/useSharedNow";
import { useTabVisibility } from "@/hooks/useTabVisibility";
import { useUserEngagement } from "@/hooks/useUserEngagement";
import { useTierOneScheduler } from "@/hooks/useTierOneScheduler";
import { getMarketState } from "@/lib/market-status";
import { useAuthStore } from "@/store/auth-store";
import { useRefreshBus } from "@/store/refresh-bus-store";

/**
 * IdleSentinel — mounts the engagement tracker + idle modal as a
 * shell-level concern. Place it at the top of every authenticated
 * shell (Mobile + Desktop). Unobtrusive: renders nothing while the
 * user is active.
 *
 * Responsibilities:
 *   • Run `useUserEngagement` so pointer / key / scroll activity is
 *     tracked across the whole authenticated app.
 *   • Open the IdleConfirmDialog when engagement enters `hard-idle`
 *     AND the tab is visible AND nothing else is suppressing it.
 *   • Translate the modal's two actions into refresh-bus events:
 *       - Refresh → `requestT1Refresh()` and reset engagement.
 *       - Continue → `markStaleAcknowledged()` and reset engagement
 *         (so the timer starts over; the hero keeps a warning dot
 *         until a real refresh lands).
 *   • Stay silent entirely when the user is signed out (no point in
 *     prompting an idle timer on the sign-in screen).
 */
export function IdleSentinel() {
  const signedIn = useAuthStore((s) => s.signedIn);
  const visibility = useTabVisibility();
  const { engagement, lastInteractionAt, bump } = useUserEngagement();
  const requestT1Refresh = useRefreshBus((s) => s.requestT1Refresh);
  const markStaleAcknowledged = useRefreshBus(
    (s) => s.markStaleAcknowledged
  );

  // Drive the T1 refresh cadence from this shell-level mount. One
  // scheduler per shell; multi-mount would multiply intervals.
  useTierOneScheduler({ visibility, engagement });

  // The dialog's "open" is gated by engagement + visibility + auth.
  // We also remember "explicitly dismissed this idle cycle" so that
  // after the user clicks Continue, the modal doesn't immediately
  // re-open on the next watchdog tick (which could happen because
  // engagement stays at hard-idle until a real user input fires).
  const [dismissedThisCycle, setDismissedThisCycle] = useState(false);

  // Reset the dismiss flag whenever engagement drops back out of
  // hard-idle — any real interaction resets, and also signOut.
  useEffect(() => {
    if (engagement !== "hard-idle") setDismissedThisCycle(false);
  }, [engagement]);

  // Suppress the modal while the market is closed (Sergey 2026-04-25).
  // No new data publishes outside RTH (or on weekends / NYSE holidays),
  // so prompting "refresh now?" is pure noise — the user already has the
  // most recent values. Subscribing to useSharedNow() gives us a 60s
  // re-evaluation tick so the gate transitions cleanly when the market
  // opens (modal can fire) or closes (modal auto-dismisses if it was
  // already up). The tick is paused while the tab is hidden, which is
  // fine because visibility === "visible" is also part of the gate.
  const now = useSharedNow();
  const marketState = getMarketState(now);

  const shouldOpen =
    signedIn &&
    visibility === "visible" &&
    engagement === "hard-idle" &&
    marketState === "open" &&
    !dismissedThisCycle;

  const idleMs =
    typeof Date !== "undefined" ? Date.now() - lastInteractionAt : 0;

  const onRefresh = () => {
    requestT1Refresh();
    // Reset engagement as if the user interacted — they did, by
    // tapping Refresh. The modal's own `open` flips false as a
    // result of the engagement change.
    bump();
  };
  const onDismiss = () => {
    markStaleAcknowledged();
    // Park the flag so re-evaluation doesn't re-open immediately.
    // Real user interaction flips engagement → effect above clears
    // this flag.
    setDismissedThisCycle(true);
  };

  return (
    <IdleConfirmDialog
      open={shouldOpen}
      idleMs={idleMs}
      onRefresh={onRefresh}
      onDismiss={onDismiss}
    />
  );
}
